import { createContext, useContext, useState, useCallback, useRef, useEffect, type ReactNode } from 'react'
import { CheckCircle, AlertCircle, Info, Wifi, RefreshCw, X } from 'lucide-react'
import './Toast.css'

type ToastType = 'success' | 'error' | 'info' | 'offline' | 'sync'

interface ToastItem {
    id: number
    message: string
    type: ToastType
    exiting?: boolean
    action?: { label: string; onClick: () => void }
}

interface AddToastOptions {
    type?: ToastType
    duration?: number
    action?: { label: string; onClick: () => void }
}

type AddToastFn = (message: string, options?: AddToastOptions) => number

const ToastContext = createContext<AddToastFn | null>(null)

let toastId = 0

export function ToastProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<ToastItem[]>([])
    const timers = useRef<Record<number, ReturnType<typeof setTimeout>>>({})
    const recentMessages = useRef<Set<string>>(new Set())

    const addToast = useCallback<AddToastFn>((message, { type = 'info', duration = 3500, action } = {}) => {
        // Ref-based dedup: block identical messages within 500ms
        if (recentMessages.current.has(message)) return -1
        recentMessages.current.add(message)
        setTimeout(() => recentMessages.current.delete(message), 500)

        const id = ++toastId
        setToasts(prev => [...prev, { id, message, type, action }])
        timers.current[id] = setTimeout(() => {
            // Start exit animation, then remove after transition
            setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t))
            setTimeout(() => {
                setToasts(prev => prev.filter(t => t.id !== id))
                delete timers.current[id]
            }, 300)
        }, duration)
        return id
    }, [])

    const removeToast = useCallback((id: number) => {
        clearTimeout(timers.current[id])
        delete timers.current[id]
        setToasts(prev => prev.filter(t => t.id !== id))
    }, [])

    // Cleanup all timers on unmount
    useEffect(() => {
        return () => Object.values(timers.current).forEach(clearTimeout)
    }, [])

    return (
        <ToastContext.Provider value={addToast}>
            {children}
            <div className="toast-container" role="status" aria-live="polite">
                {toasts.map(t => (
                    <ToastDisplay key={t.id} toast={t} onClose={() => removeToast(t.id)} />
                ))}
            </div>
        </ToastContext.Provider>
    )
}

const ICONS: Record<ToastType, ReactNode> = {
    success: <CheckCircle size={16} />,
    error: <AlertCircle size={16} />,
    info: <Info size={16} />,
    offline: <Wifi size={16} />,
    sync: <RefreshCw size={16} />,
}

function ToastDisplay({ toast, onClose }: { toast: ToastItem; onClose: () => void }) {
    return (
        <div className={`toast toast-${toast.type}${toast.exiting ? ' toast-exiting' : ''}`}>
            <span className="toast-icon">{ICONS[toast.type] || ICONS.info}</span>
            <span className="toast-message">{toast.message}</span>
            {toast.action && (
                <button
                    className="toast-action"
                    onClick={(e) => {
                        e.stopPropagation()
                        toast.action?.onClick()
                        onClose()
                    }}
                >
                    {toast.action.label}
                </button>
            )}
            <button className="toast-close" onClick={onClose} aria-label="Close">
                <X size={14} />
            </button>
        </div>
    )
}

export function useToast(): AddToastFn {
    const addToast = useContext(ToastContext)
    if (!addToast) throw new Error('useToast must be used within ToastProvider')
    return addToast
}
